<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import AmountField from './AmountField.vue'
import FundingMethodOption from './FundingMethodOption.vue'
import MoneyModalHeader from './MoneyModalHeader.vue'
import DepositBankTransferStep from './DepositBankTransferStep.vue'
import DepositConfirmStep from './DepositConfirmStep.vue'
import DepositSubmittedStep from './DepositSubmittedStep.vue'
import { maskAccountNumber } from '@shared/utils'
import {
  DEFAULT_DEPOSIT_AMOUNT,
  FUNDING_METHODS,
  type FundingMethod,
  type FundingMethodId,
} from '../constants/fiatWalletConstants'
import type { SlicealAccount } from '../types/fiatWalletTypes'
import type { BankAccount } from '@modules/account'

type DepositStep = 1 | 2 | 3 | 4

interface Props {
  visible: boolean
  currency?: string
  /** Formatted current balance, e.g. `"45,000.00"`. */
  balance?: string
  /** Shown as the Linked Bank Account subtitle when one exists. */
  defaultBankAccount?: BankAccount | null
  slicealAccount?: SlicealAccount | null
  submitting?: boolean
  submitError?: string | null
  /** Set by the container once the deposit is accepted — advances to step 4. */
  submittedAt?: string
  /** Reference the deposit was filed under, shown on the receipt. */
  submittedReference?: string
}

const props = withDefaults(defineProps<Props>(), {
  currency: 'AED',
  balance: '',
  defaultBankAccount: null,
  slicealAccount: null,
  submitting: false,
  submitError: null,
  submittedAt: '',
  submittedReference: '',
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', payload: { amount: number; attachment: File }): void
  (e: 'clear-error'): void
  (e: 'view-history'): void
}>()

const step = ref<DepositStep>(1)
const amount = ref(DEFAULT_DEPOSIT_AMOUNT)
const methodId = ref<FundingMethodId>('bank_transfer')
const amountError = ref('')
const confirmStepRef = ref<InstanceType<typeof DepositConfirmStep> | null>(null)

const STEP_TITLE: Record<DepositStep, string> = {
  1: 'Add Funds',
  2: 'Bank Transfer',
  3: 'Confirm Transfer',
  4: 'Deposit Submitted',
}

const title = computed(() => STEP_TITLE[step.value])

const amountFormatted = computed(() =>
  amount.value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
)

// Only the linked-bank row gets a dynamic subtitle — when the investor has a
// bank on file it names it, so the row reads like the design.
const methods = computed<FundingMethod[]>(() =>
  FUNDING_METHODS.map((method) => {
    if (method.id !== 'linked_bank' || !props.defaultBankAccount) return method
    const bank = props.defaultBankAccount
    return {
      ...method,
      subtitle: `${bank.bankName} ${maskAccountNumber(bank.accountNumber)}`,
    }
  }),
)

watch(amount, () => {
  if (amountError.value) amountError.value = ''
})

// The container reports acceptance by setting `submittedAt`; that is the signal
// to move to the receipt step rather than closing the dialog.
watch(
  () => props.submittedAt,
  (value) => {
    if (value) step.value = 4
  },
)

// Reset the whole flow whenever the dialog closes so re-opening never resumes
// mid-way through a transfer the investor abandoned.
watch(
  () => props.visible,
  (open) => {
    if (open) return
    step.value = 1
    amount.value = DEFAULT_DEPOSIT_AMOUNT
    methodId.value = 'bank_transfer'
    amountError.value = ''
    if (props.submitError) emit('clear-error')
  },
)

const close = () => emit('update:visible', false)

const onDialogUpdate = (value: boolean) => {
  if (!value && !props.submitting) close()
}

const onSelectMethod = (id: FundingMethodId) => {
  methodId.value = id
}

const onContinue = () => {
  if (amount.value <= 0) {
    amountError.value = `Enter an amount greater than ${props.currency} 0.`
    return
  }
  amountError.value = ''
  step.value = 2
}

const onMadeTransfer = () => {
  step.value = 3
}

const onConfirm = () => {
  const payload = confirmStepRef.value?.collect()
  if (!payload) return
  emit('confirm', { amount: amount.value, ...payload })
}

const onViewHistory = () => {
  emit('view-history')
  close()
}
</script>

<template>
  <Dialog
    :model-value="visible"
    width="660px"
    :padded="false"
    :show-close-icon="false"
    :dismissable="!submitting"
    @update:model-value="onDialogUpdate"
  >
    <Box direction="col" class="afm">
      <MoneyModalHeader :title="title" :disabled="submitting" @close="close" />

      <Box class="afm__divider" />

      <Box direction="col" gap="6" class="afm__body">
        <template v-if="step === 1">
          <Box direction="col" gap="1">
            <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
              Current Balance
            </Text>
            <Text type="display" weight="semibold" is="p" class="afm__balance">
              <CurrencyText :text="`${currency} ${balance || '0.00'}`" />
            </Text>
          </Box>

          <Box direction="col" gap="3">
            <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
              Deposit Amount
            </Text>
            <AmountField v-model="amount" :currency="currency" :invalid="!!amountError" />
            <Box v-if="amountError" align="center" gap="1">
              <PiIcon name="exclamation-circle" class="afm__error-icon" />
              <Text type="caption" color="danger" is="span"
                ><CurrencyText :text="amountError"
              /></Text>
            </Box>
          </Box>

          <Box direction="col" gap="3">
            <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
              Funding Method
            </Text>
            <Box direction="col" gap="3">
              <FundingMethodOption
                v-for="method in methods"
                :key="method.id"
                :method="method"
                :selected="method.id === methodId"
                @select="onSelectMethod"
              />
            </Box>
            <Text type="caption" color="muted" is="p">
              Card, digital wallet and linked-bank funding are on the way. Bank transfer is
              available today — you'll get SliceAL's escrow details on the next step.
            </Text>
          </Box>
        </template>

        <DepositBankTransferStep
          v-else-if="step === 2"
          :amount="amountFormatted"
          :currency="currency"
          :sliceal-account="slicealAccount"
        />

        <DepositConfirmStep
          v-else-if="step === 3"
          ref="confirmStepRef"
          :amount="amountFormatted"
          :currency="currency"
          :submit-error="submitError"
          :submitting="submitting"
        />

        <DepositSubmittedStep
          v-else
          :amount="amountFormatted"
          :currency="currency"
          :bank-reference="submittedReference"
          :submitted-at="submittedAt"
        />
      </Box>

      <Box class="afm__divider" />

      <Box gap="3" class="afm__footer">
        <template v-if="step === 4">
          <AppButton
            label="View Transaction History"
            color="secondary"
            variant="outline"
            size="large"
            class="afm__footer-btn"
            @click="onViewHistory"
          />
          <AppButton label="Done" size="large" class="afm__footer-btn" @click="close" />
        </template>

        <AppButton
          v-else-if="step === 3"
          label="Confirm Transfer"
          size="large"
          block
          :loading="submitting"
          @click="onConfirm"
        />

        <AppButton
          v-else-if="step === 2"
          label="I Have Made the Transfer"
          size="large"
          block
          @click="onMadeTransfer"
        />

        <AppButton
          v-else
          label="Continue"
          icon="arrow-right"
          icon-pos="right"
          size="large"
          block
          @click="onContinue"
        />
      </Box>
    </Box>
  </Dialog>
</template>

<style scoped>
.afm {
  max-height: calc(100vh - 6rem);
}

.afm__divider {
  flex-shrink: 0;
  height: 1px;
  background: var(--p-content-border-color);
}

.afm__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1.5rem 1.75rem;
  box-sizing: border-box;
}

.afm__balance {
  font-variant-numeric: tabular-nums;
}

.afm__footer {
  flex-shrink: 0;
  padding: 1.25rem 1.75rem 1.5rem;
}

.afm__footer-btn {
  flex: 1;
  min-width: 0;
}

.afm__error-icon {
  font-size: 12px;
  color: var(--p-danger-600);
}

@media (max-width: 992px) {
  .afm__body {
    padding: 1.25rem 1rem;
  }

  .afm__footer {
    padding: 1rem;
    flex-direction: column-reverse;
  }
}
</style>
